import { type AccessRule, defaultRoute, routes } from '@/shared/config';
import type { SessionUser, UserRole } from './types';

// Role rules of the API: user management and the full ticket list are ADMIN/SUPERADMIN only
// (user.routes.ts, tickets.routes.ts); RPA accounts are not administrators there.
export const ADMIN_ROLES: readonly UserRole[] = ['ADMIN', 'SUPERADMIN'];

export const isAdmin = (user: SessionUser | null | undefined) =>
  !!user && ADMIN_ROLES.includes(user.role);

/**
 * Analytics-only accounts may only use the marketing analytics screens. The API refuses every
 * other module for them regardless of role (helpers/permissionType.ts `denyAnalyticsUser`).
 */
export const isAnalyticsOnly = (user: SessionUser | null | undefined) =>
  user?.permissionType === 'ANALYTICS';

/** Same rule as the API's `requireBotConfigAccess`. */
export const canConfigureBot = (user: SessionUser | null | undefined) =>
  isAdmin(user) || user?.permissionType === 'BOT_CONFIGURATION';

/** Where a signed-in user lands: analytics-only accounts start on the analytics screens. */
export const homeRoute = (user: SessionUser | null | undefined): string =>
  isAnalyticsOnly(user) ? routes.analyticsCalls : defaultRoute;

/** Whether an analytics-only account may open `pathname` (analytics screens and the profile). */
export const isAnalyticsPath = (pathname: string) =>
  pathname === routes.analytics ||
  pathname.startsWith(`${routes.analytics}/`) ||
  pathname === routes.profile;

export function canAccess(user: SessionUser | null | undefined, rule: AccessRule): boolean {
  if (!user) return false;
  switch (rule) {
    case 'admin':
      return isAdmin(user);
    case 'botConfiguration':
      return canConfigureBot(user);
    default:
      return true;
  }
}
